import { useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2, Plane, Route } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ConsentCheckboxes } from '../components/ConsentCheckboxes';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { useLang } from '../i18n';
import { useSEO } from '../seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'eco-input';
const initial = {
  departure_country: '', departure_airport: '', destination: 'São Tomé', departure_date: '', return_date: '',
  adults: '2', children: '0', cabin_preference: '', flexible_dates: false, inter_island_flight_assistance: false,
  name: '', email: '', whatsapp: '', country: '', message: '',
};

export default function FlightsPage() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [consents, setConsents] = useState({ terms: false, cancellation: false, marketing: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  useSEO(t('flights.seo.title'), t('flights.seo.description'));

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      const response = await fetch(`${API}/requests`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'flight_assistance', source: 'flights_page', product: t('flights.request'),
          ...form, flight_assistance: true, travelers: String(Number(form.adults || 0) + Number(form.children || 0)),
          consent_terms: consents.terms, consent_cancellation: consents.cancellation, consent_marketing: consents.marketing,
        }),
      });
      if (!response.ok) throw new Error('request failed');
      setDone(await response.json());
    } catch {
      toast.error(t('flights.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="flights-page">
      <PageHeader kicker={t('flights.kicker')} title={t('flights.title')} sub={t('flights.subtitle')} />
      <section className="bg-eco-cream py-14 md:py-20">
        <div className="eco-container grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="space-y-5">
            <Reveal className="rounded-2xl border border-eco-ink/10 bg-white p-6">
              <Plane className="text-eco-gold" size={24} />
              <h2 className="mt-4 text-xl font-semibold text-eco-ink">{t('flights.international.title')}</h2>
              <p className="mt-2 text-sm leading-relaxed text-eco-ink/60">{t('flights.international.text')}</p>
            </Reveal>
            <Reveal className="rounded-2xl border border-eco-ink/10 bg-white p-6" delay={0.08}>
              <Route className="text-eco-gold" size={24} />
              <h2 className="mt-4 text-xl font-semibold text-eco-ink">{t('flights.interisland.title')}</h2>
              <p className="mt-2 text-sm leading-relaxed text-eco-ink/60">{t('flights.interisland.text')}</p>
              <p className="mt-3 text-xs font-semibold text-eco-green">{t('flights.availability')}</p>
            </Reveal>
          </div>
          <Reveal className="rounded-2xl border border-eco-ink/10 bg-white p-6 shadow-sm md:p-8">
            {done ? (
              <div className="py-12 text-center" data-testid="flight-request-success">
                <CheckCircle2 size={38} className="mx-auto text-eco-green" />
                <h2 className="mt-4 text-2xl font-semibold">{t('flights.sent')}</h2>
                <p className="mt-2 text-sm text-eco-ink/60">{t('flights.sentText')}</p>
                <p className="mt-5 font-semibold text-eco-green">{done.reference}</p>
                <Link to="/build-your-journey" className="eco-btn-secondary mt-7">{t('journey.build')} <ArrowRight size={14} /></Link>
              </div>
            ) : (
              <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="flight-assistance-form">
                <h2 className="sm:col-span-2 text-2xl font-semibold">{t('flights.request')}</h2>
                <label><span className="eco-field-label">{t('flights.departureCountry')}</span><input required value={form.departure_country} onChange={(e) => set('departure_country', e.target.value)} className={inputCls} /></label>
                <label><span className="eco-field-label">{t('flights.departureAirport')}</span><input required value={form.departure_airport} onChange={(e) => set('departure_airport', e.target.value)} className={inputCls} /></label>
                <label><span className="eco-field-label">{t('wiz.destination')}</span><select value={form.destination} onChange={(e) => set('destination', e.target.value)} className={inputCls}><option>São Tomé</option><option>Príncipe</option><option>São Tomé + Príncipe</option></select></label>
                <label><span className="eco-field-label">{t('flights.cabin')}</span><select value={form.cabin_preference} onChange={(e) => set('cabin_preference', e.target.value)} className={inputCls}><option value="">{t('flights.noPreference')}</option><option>Economy</option><option>Premium Economy</option><option>Business</option></select></label>
                <label><span className="eco-field-label">{t('flights.outbound')}</span><input required type="date" value={form.departure_date} onChange={(e) => set('departure_date', e.target.value)} className={inputCls} /></label>
                <label><span className="eco-field-label">{t('flights.return')}</span><input type="date" value={form.return_date} onChange={(e) => set('return_date', e.target.value)} className={inputCls} /></label>
                <label><span className="eco-field-label">{t('form.adults')}</span><input min="1" type="number" value={form.adults} onChange={(e) => set('adults', e.target.value)} className={inputCls} /></label>
                <label><span className="eco-field-label">{t('form.children')}</span><input min="0" type="number" value={form.children} onChange={(e) => set('children', e.target.value)} className={inputCls} /></label>
                <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.flexible_dates} onChange={(e) => set('flexible_dates', e.target.checked)} />{t('flights.flexible')}</label>
                <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.inter_island_flight_assistance} onChange={(e) => set('inter_island_flight_assistance', e.target.checked)} />{t('flights.interisland.option')}</label>
                <label><span className="eco-field-label">{t('form.name')}</span><input required value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} /></label>
                <label><span className="eco-field-label">{t('form.email')}</span><input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} /></label>
                <label><span className="eco-field-label">{t('wiz.wa.ph')}</span><input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className={inputCls} /></label>
                <label><span className="eco-field-label">{t('form.country')}</span><input value={form.country} onChange={(e) => set('country', e.target.value)} className={inputCls} /></label>
                <label className="sm:col-span-2"><span className="eco-field-label">{t('form.message')}</span><textarea rows={3} value={form.message} onChange={(e) => set('message', e.target.value)} className={inputCls} /></label>
                <div className="sm:col-span-2"><ConsentCheckboxes testid="flight-consent" value={consents} onChange={setConsents} /></div>
                <button disabled={sending || !consents.terms} className="eco-btn-primary sm:col-span-2 justify-center disabled:opacity-50">{sending ? <Loader2 className="animate-spin" size={16} /> : t('flights.submit')} <ArrowRight size={15} /></button>
                <p className="sm:col-span-2 text-xs text-eco-ink/50">{t('flights.disclaimer')}</p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
